import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Users, Luggage, Check, Phone, ArrowRight, Star } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { FleetGallery } from "@/components/FleetGallery";
import { FLEET, COMPANY, BOOKING_URL } from "@/data/site";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

export default function FleetDetail() {
  const { slug } = useParams();
  const vehicle = FLEET.find((v) => v.slug === slug);
  if (!vehicle) return <Navigate to="/fleet" replace />;

  const others = FLEET.filter((v) => v.slug !== slug);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: `${vehicle.name} — Clocktower Cars Gatwick`,
      description: vehicle.desc,
      image: `${origin}${vehicle.image}`,
      brand: { "@type": "Brand", name: COMPANY.name },
      category: "Private hire vehicle",
      offers: {
        "@type": "Offer",
        priceCurrency: "GBP",
        price: "25",
        availability: "https://schema.org/InStock",
        url: `${origin}/fleet/${vehicle.slug}`,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: "Fleet", item: `${origin}/fleet` },
        { "@type": "ListItem", position: 3, name: vehicle.name, item: `${origin}/fleet/${vehicle.slug}` },
      ],
    },
  ];

  return (
    <Layout>
      <Seo
        title={`${vehicle.name} — ${vehicle.tier}`}
        description={`${vehicle.tagline}. Fixed-fare ${vehicle.name.toLowerCase()} private hire with a licensed, DBS-checked chauffeur across Gatwick, Crawley and Surrey.`}
        path={`/fleet/${vehicle.slug}`}
        image={vehicle.image}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative bg-ink py-14 md:py-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[420px] w-[820px] rounded-full bg-brand/15 blur-[130px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Link to="/fleet" data-testid="fleet-detail-back" className="inline-flex items-center gap-2 text-white/60 hover:text-brand-light transition-colors mb-6 text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to fleet
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <motion.div {...fadeUp} className="lg:col-span-6">
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-brand-light mb-4">{vehicle.tier}</p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[0.95]">
                {vehicle.name}
              </h1>
              <p className="mt-5 text-lg text-white/70 max-w-lg leading-relaxed">
                {vehicle.tagline}.
              </p>

              <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-sm font-medium text-white/70">
                <span className="flex items-center gap-2"><Users className="h-4 w-4 text-brand-light" /> Up to {vehicle.seats} passengers</span>
                <span className="flex items-center gap-2"><Luggage className="h-4 w-4 text-brand-light" /> {vehicle.luggage} large bags</span>
                <span className="flex items-center gap-2"><Star className="h-4 w-4 text-brand-light fill-brand-light" /> {vehicle.year} model</span>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a href={BOOKING_URL} data-testid="fleet-detail-book-btn" className="bg-brand hover:bg-brand-light text-white px-8 py-4 rounded-full font-bold transition-all duration-300 hover:shadow-[0_8px_24px_rgba(31,157,42,0.4)] active:scale-95">
                  Book a {vehicle.name}
                </a>
                <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-white text-white px-8 py-4 rounded-full font-bold transition-colors">
                  <Phone className="h-4 w-4 text-brand-light" /> {COMPANY.phone}
                </a>
              </div>
            </motion.div>

            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:col-span-6">
              <FleetGallery images={vehicle.gallery || [{ src: vehicle.image, alt: vehicle.name, caption: "Exterior" }]} vehicleName={vehicle.name} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* OVERVIEW + SPECS */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand mb-3">The vehicle</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-ink tracking-tight mb-5">About the {vehicle.name}</h2>
            <p className="text-ink/70 leading-relaxed text-lg mb-6">{vehicle.desc}</p>
            <p className="text-ink/60 leading-relaxed">
              Typical vehicles: <span className="text-ink font-semibold">{vehicle.model}</span>. Actual model
              may vary but every vehicle is a modern, professionally-maintained {vehicle.year} example driven by
              a licensed, DBS-checked chauffeur.
            </p>

            <h3 className="font-heading text-xl font-bold text-ink mt-10 mb-5">Perfect for</h3>
            <ul className="space-y-3">
              {vehicle.perfectFor.map((p) => (
                <li key={p} className="flex items-start gap-3 text-ink/75 leading-relaxed">
                  <span className="grid place-items-center h-6 w-6 rounded-full bg-brand/10 text-brand shrink-0 mt-0.5"><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>
                  {p}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="lg:col-span-5">
            <div className="bg-surface rounded-2xl p-7 border border-[#EAEBEA]">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand mb-4">At a glance</p>
              <dl className="space-y-4">
                {vehicle.specs.map((s) => (
                  <div key={s.label} className="flex items-center justify-between gap-4 pb-4 border-b border-[#EAEBEA] last:border-b-0 last:pb-0">
                    <dt className="text-sm text-ink/50 font-medium">{s.label}</dt>
                    <dd className="text-ink font-semibold text-right text-sm">{s.value}</dd>
                  </div>
                ))}
              </dl>

              <a href={BOOKING_URL} className="mt-6 block w-full text-center py-3.5 bg-brand hover:bg-brand-light text-white rounded-full font-bold transition-colors">
                Book online now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-surface py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand mb-3">On board</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-ink tracking-tight">What's included in every {vehicle.name} journey</h2>
          </motion.div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {vehicle.features.map((f, i) => (
              <motion.div key={f} {...fadeUp} transition={{ ...fadeUp.transition, delay: (i % 3) * 0.05 }} data-testid={`fleet-feature-${i}`} className="flex items-start gap-4 bg-white border border-[#EAEBEA] rounded-2xl p-5">
                <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0"><Check className="h-5 w-5" strokeWidth={2.5} /></span>
                <p className="text-ink/75 leading-relaxed text-[15px]">{f}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* OTHER VEHICLES */}
      <section className="bg-white py-16 md:py-20 border-t border-[#EAEBEA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand mb-3">Explore the fleet</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-ink tracking-tight">Other vehicles</h2>
            </div>
            <Link to="/fleet" className="group inline-flex items-center gap-2 text-ink font-semibold hover:text-brand transition-colors text-sm">
              View all vehicles <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="grid gap-5 md:grid-cols-3 lg:grid-cols-5">
            {others.map((v) => (
              <Link key={v.slug} to={`/fleet/${v.slug}`} data-testid={`fleet-related-${v.slug}`} className="group block bg-white border border-[#EAEBEA] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.12)] transition-all duration-300">
                <div className="h-32 overflow-hidden">
                  <img src={v.image} alt={v.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-[600ms]" />
                </div>
                <div className="p-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-brand">{v.tier}</p>
                  <h3 className="font-heading text-base font-bold text-ink mt-1 group-hover:text-brand transition-colors">{v.name}</h3>
                  <p className="text-xs text-ink/50 mt-1 flex items-center gap-3">
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {v.seats}</span>
                    <span className="flex items-center gap-1"><Luggage className="h-3 w-3" /> {v.luggage}</span>
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-ink py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight">
            Ready to travel in a {vehicle.name}?
          </h2>
          <p className="mt-4 text-white/60 max-w-xl mx-auto leading-relaxed">
            Book online in under a minute or call our 24/7 team.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a href={BOOKING_URL} data-testid="fleet-detail-final-book" className="bg-brand hover:bg-brand-light text-white px-9 py-4 rounded-full font-bold transition-all duration-300 hover:shadow-[0_8px_24px_rgba(31,157,42,0.35)]">
              Book online now
            </a>
            <a href={`tel:${COMPANY.phoneRaw}`} className="border-2 border-white/25 hover:border-white text-white px-9 py-4 rounded-full font-bold transition-colors inline-flex items-center gap-2">
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
